import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SEOHead, { SeoBreadcrumb } from './SEOHead';
import {
  BRAND_DESCRIPTION,
  BRAND_KEYWORDS,
  BRAND_NAME,
  BRAND_TITLE,
  GRADE_SEO,
} from '../../config/brand';

type PageSeo = {
  title: string;
  description: string;
  keywords?: string;
  noIndex?: boolean;
  breadcrumbs?: SeoBreadcrumb[];
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
};

const withBrandKeywords = (extra?: string) =>
  extra ? `${BRAND_KEYWORDS}, ${extra}` : BRAND_KEYWORDS;

const DEFAULT: PageSeo = {
  title: BRAND_TITLE,
  description: BRAND_DESCRIPTION,
  keywords: BRAND_KEYWORDS,
  breadcrumbs: [],
};

function gradeFromPath(pathname: string, prefix: '/courses/grade/' | '/books/grade/') {
  if (!pathname.startsWith(prefix)) return null;
  return pathname.slice(prefix.length).split('/')[0] || null;
}

/**
 * Resolve SEO for the current path so every public route is indexable with rich metadata.
 */
function resolvePageSeo(pathname: string): PageSeo {
  if (pathname === '/') {
    return {
      title: BRAND_TITLE,
      description: BRAND_DESCRIPTION,
      keywords: withBrandKeywords('تعليم أونلاين, كورسات كل المراحل'),
      breadcrumbs: [],
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'ما هي منصة أوائل مصر؟',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'منصة تعليمية أونلاين لكل طلاب مصر بمختلف المراحل الدراسية — ابتدائي وإعدادي وثانوي — في كل المواد.',
            },
          },
          {
            '@type': 'Question',
            name: 'لأي صفوف تقدّم المنصة كورسات؟',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'تقدّم المنصة كورسات لجميع المراحل الدراسية: ابتدائي وإعدادي وثانوي.',
            },
          },
          {
            '@type': 'Question',
            name: 'هل يمكن لولي الأمر متابعة الطالب؟',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'نعم عبر صفحة متابعة ولي الأمر باستخدام أرقام الهواتف المسجّلة.',
            },
          },
        ],
      },
    };
  }

  if (pathname === '/login') {
    return {
      title: `تسجيل الدخول | ${BRAND_NAME}`,
      description: `سجّل دخولك إلى منصة أوائل مصر لمتابعة كورساتك وحصصك أونلاين.`,
      keywords: withBrandKeywords('تسجيل دخول أوائل مصر'),
      noIndex: true,
      breadcrumbs: [{ name: 'تسجيل الدخول', path: '/login' }],
    };
  }

  if (pathname === '/register') {
    return {
      title: `إنشاء حساب | ${BRAND_NAME}`,
      description: `أنشئ حسابك مجاناً في منصة أوائل مصر وابدأ التعلم في كل المراحل وكل المواد.`,
      keywords: withBrandKeywords('إنشاء حساب, تسجيل طالب'),
      noIndex: true,
      breadcrumbs: [{ name: 'إنشاء حساب', path: '/register' }],
    };
  }

  if (pathname === '/forgot-password') {
    return {
      title: 'استعادة كلمة المرور',
      description: 'استعد كلمة مرور حسابك في منصة أوائل مصر بسهولة وأمان.',
      noIndex: true,
    };
  }

  if (pathname === '/reset-password') {
    return {
      title: 'إعادة تعيين كلمة المرور',
      description: 'أعد تعيين كلمة المرور الخاصة بحسابك في منصة أوائل مصر.',
      noIndex: true,
    };
  }

  if (pathname === '/contact') {
    return {
      title: `تواصل معنا | ${BRAND_NAME}`,
      description: `تواصل مع منصة أوائل مصر عبر البريد الإلكتروني لدعم الطلاب وأولياء الأمور.`,
      keywords: withBrandKeywords('تواصل أوائل مصر, دعم الطلاب'),
      breadcrumbs: [{ name: 'تواصل معنا', path: '/contact' }],
    };
  }

  if (pathname === '/privacy') {
    return {
      title: `سياسة الخصوصية | ${BRAND_NAME}`,
      description: `سياسة خصوصية منصة أوائل مصر: جمع واستخدام بيانات الطلاب بأمان.`,
      keywords: withBrandKeywords('سياسة الخصوصية'),
      breadcrumbs: [{ name: 'سياسة الخصوصية', path: '/privacy' }],
    };
  }

  if (pathname === '/terms') {
    return {
      title: `الشروط والأحكام | ${BRAND_NAME}`,
      description: `شروط استخدام منصة أوائل مصر — الحسابات، الاشتراكات، والمحتوى التعليمي.`,
      keywords: withBrandKeywords('شروط الاستخدام'),
      breadcrumbs: [{ name: 'الشروط والأحكام', path: '/terms' }],
    };
  }

  if (pathname === '/refund-policy') {
    return {
      title: `سياسة الاسترجاع | ${BRAND_NAME}`,
      description: `سياسة الاسترجاع في منصة أوائل مصر وشروط طلب استرداد رسوم الكورسات أونلاين.`,
      keywords: withBrandKeywords('سياسة الاسترجاع, استرداد رسوم'),
      breadcrumbs: [{ name: 'سياسة الاسترجاع', path: '/refund-policy' }],
    };
  }

  if (pathname === '/parent-monitoring') {
    return {
      title: `متابعة ولي الأمر | ${BRAND_NAME}`,
      description: `تابع تقدم ابنك في منصة أوائل مصر واطلع على نشاطه في الدروس.`,
      keywords: withBrandKeywords('متابعة ولي الأمر, تقدم الطالب'),
      breadcrumbs: [{ name: 'متابعة ولي الأمر', path: '/parent-monitoring' }],
    };
  }

  const courseGrade = gradeFromPath(pathname, '/courses/grade/');
  if (courseGrade) {
    const g = GRADE_SEO[courseGrade];
    const gradeLabel = g?.short || g?.title || 'كورسات حسب الصف';
    return {
      title: `كورسات ${gradeLabel} | ${BRAND_NAME}`,
      description: `اشترك في كورسات ${g?.title || gradeLabel} على منصة أوائل مصر — دروس أونلاين لكل المواد.`,
      keywords: withBrandKeywords(
        `كورسات ${gradeLabel}, ${g?.keywords || ''}, كورسات أونلاين`
      ),
      breadcrumbs: [
        { name: 'الكورسات', path: `/courses/grade/${courseGrade}` },
        { name: g?.title || gradeLabel, path: `/courses/grade/${courseGrade}` },
      ],
    };
  }

  const bookGrade = gradeFromPath(pathname, '/books/grade/');
  if (bookGrade) {
    const g = GRADE_SEO[bookGrade];
    const gradeLabel = g?.short || g?.title || 'كتب حسب الصف';
    return {
      title: `كتب ${gradeLabel} | ${BRAND_NAME}`,
      description: `كتب ومراجع ${g?.title || gradeLabel} من منصة أوائل مصر.`,
      keywords: withBrandKeywords(`كتب ${gradeLabel}, مراجع دراسية, ${g?.keywords || ''}`),
      breadcrumbs: [
        { name: 'الكتب', path: `/books/grade/${bookGrade}` },
        { name: g?.title || gradeLabel, path: `/books/grade/${bookGrade}` },
      ],
    };
  }

  if (pathname.startsWith('/courses/')) {
    return {
      title: `تفاصيل الكورس | ${BRAND_NAME}`,
      description: `شاهد تفاصيل الكورس واشترك على منصة أوائل مصر — تعليم أونلاين لكل المراحل.`,
      keywords: withBrandKeywords('تفاصيل كورس, كورسات أونلاين'),
      breadcrumbs: [{ name: 'تفاصيل الكورس', path: pathname }],
    };
  }

  if (pathname.startsWith('/units/')) {
    return {
      title: `تفاصيل الحصة | ${BRAND_NAME}`,
      description: `حصة مستقلة على منصة أوائل مصر — محتوى تعليمي أونلاين لطلاب كل المراحل.`,
      keywords: withBrandKeywords('حصة مستقلة, دروس أونلاين, حصص أونلاين'),
      breadcrumbs: [{ name: 'تفاصيل الحصة', path: pathname }],
    };
  }

  if (pathname.startsWith('/teacher/')) {
    return {
      title: `صفحة المعلم | ${BRAND_NAME}`,
      description: `تعرّف على معلمي منصة أوائل مصر وكورساتهم في كل المواد والمراحل الدراسية.`,
      keywords: withBrandKeywords('معلمون, مدرسين أوائل مصر'),
      breadcrumbs: [{ name: 'المعلمون', path: pathname }],
    };
  }

  if (pathname.startsWith('/subject/')) {
    return {
      title: `المعلمون حسب المادة | ${BRAND_NAME}`,
      description: `اختر المادة وشاهد معلمي أوائل مصر المتخصصين في كل المواد الدراسية.`,
      keywords: withBrandKeywords('مواد دراسية, معلمون حسب المادة'),
      breadcrumbs: [{ name: 'المواد', path: pathname }],
    };
  }

  if (pathname === '/checkout' || pathname === '/payment-success') {
    return {
      title: pathname === '/checkout' ? 'إتمام الشراء' : 'تم الدفع بنجاح',
      description: 'إتمام عملية الاشتراك في كورسات منصة أوائل مصر.',
      noIndex: true,
    };
  }

  if (pathname.startsWith('/student-portal')) {
    const portalMap: Record<string, PageSeo> = {
      '/student-portal': {
        title: 'بوابة الطالب - كورساتي وحصصي',
        description: 'بوابة الطالب في أوائل مصر: كورساتك وحصصك ومتابعة تقدمك التعليمي.',
        noIndex: true,
      },
      '/student-portal/browse': {
        title: 'استكشف الكورسات',
        description: 'استكشف كورسات وحصص منصة أوائل مصر من داخل بوابة الطالب.',
        noIndex: true,
      },
      '/student-portal/wallet': {
        title: 'محفظتي',
        description: 'إدارة محفظتك وشحن الرصيد في منصة أوائل مصر.',
        noIndex: true,
      },
      '/student-portal/forum': {
        title: 'المنتدى',
        description: 'منتدى الطلاب في منصة أوائل مصر للأسئلة والدعم العلمي.',
        noIndex: true,
      },
      '/student-portal/my-grades': {
        title: 'درجاتي',
        description: 'اطّلع على درجاتك ونتائجك في منصة أوائل مصر.',
        noIndex: true,
      },
    };
    if (portalMap[pathname]) return portalMap[pathname];
    if (pathname.startsWith('/student-portal/teacher/')) {
      return {
        title: 'معلم في بوابة الطالب',
        description: 'كورسات المعلم داخل بوابة طالب أوائل مصر.',
        noIndex: true,
      };
    }
    return {
      title: 'بوابة الطالب',
      description: 'بوابة الطالب في منصة أوائل مصر.',
      noIndex: true,
    };
  }

  if (
    pathname.startsWith('/course-player/') ||
    pathname.startsWith('/unit-viewer/') ||
    pathname.includes('/curriculum')
  ) {
    return {
      title: 'مشغل المحتوى',
      description: 'مشاهدة المحتوى التعليمي على منصة أوائل مصر.',
      noIndex: true,
    };
  }

  if (pathname === '/profile') {
    return {
      title: 'الملف الشخصي',
      description: 'إدارة ملفك الشخصي في منصة أوائل مصر.',
      noIndex: true,
    };
  }

  // Unknown SPA routes → soft 404 UI with noindex
  return {
    title: `الصفحة غير موجودة | ${BRAND_NAME}`,
    description: `الصفحة غير متوفرة على منصة أوائل مصر. عد للرئيسية أو تصفّح الكورسات.`,
    noIndex: true,
    breadcrumbs: [{ name: 'غير موجود', path: pathname }],
  };
}

/**
 * Global SEO for every route — mount once inside the Router.
 */
const RouteSEO: React.FC = () => {
  // Canonical must be pathname-only — query strings (utm, q, etc.) create
  // "Alternate page with proper canonical tag" entries in Search Console.
  const { pathname } = useLocation();

  const seo = useMemo(() => resolvePageSeo(pathname), [pathname]);

  return (
    <SEOHead
      title={seo.title}
      description={seo.description}
      keywords={seo.keywords ?? BRAND_KEYWORDS}
      url={pathname === '/' ? '/' : pathname.replace(/\/+$/, '')}
      noIndex={seo.noIndex}
      breadcrumbs={seo.breadcrumbs}
      structuredData={seo.structuredData}
    />
  );
};

export default RouteSEO;
